/**
 * TemplateEditorModal — create / edit one DisclosureTemplate.
 *
 * Wire endpoint: POST /v3/admin/disclosures/templates (upsert by code). The server
 * stamps UpdatedBy with the signed-in user and refuses a SignatureRequirement the
 * renderer does not know (UNKNOWN_SIGNATURE_REQUIREMENT).
 *
 * 2026-10-07 rewrite (Bill's 2026-10-06 capture of the POA_CERT edit dialog):
 *   - Every closed value is a picker fed by GET /v3/admin/disclosures/options —
 *     no free-text CSV for product types, products or customer types.
 *   - A legacy value already on the row (e.g. Multiplicity 'perCart', Content source
 *     'pcb-canonical', both absent from the old dropdowns) is kept and shown, so
 *     opening + saving no longer silently rewrites it to the first option.
 *   - The old dialog offered SignatureRequirement 'officer', which the renderer does
 *     not know and routes to EVERY signer; it is gone.
 *   - Empty sort order stays empty (the old form wrote 0).
 *
 * These template settings are not versioned: they save immediately and the next
 * package built uses them (no approval step exists for them in the code). Body,
 * page count and "only when" conditions live on versions, which go through
 * submit / approve / activate.
 */

import { useEffect, useMemo, useState } from 'react';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import {
  upsertTemplate,
  type DisclosureTemplateUpsertInput,
} from '@/services/disclosuresAdminApi';
import type { DisclosureTemplateRow } from '@/services/disclosuresApi';

import {
  Checkbox,
  ChipPicker,
  Field,
  FieldGrid,
  Modal,
  NumberInput,
  TextArea,
  TextInput,
  VocabSelect,
  type PickItem,
} from './_shared';
import { joinCsv, splitCsv } from './disclosureModel';

interface FormShape {
  code: string;
  displayName: string;
  description: string;
  layer: string;
  multiplicity: string;
  defaultPageCount: string;
  signatureRequirement: string;
  defaultSignatureCount: string;
  contentSource: string;
  variantKey: string;
  productCategories: string[];
  products: string[];
  entityTypes: string[];
  audience: string;
  sortOrder: string;
  isInternal: boolean;
  isActive: boolean;
}

const EMPTY_FORM: FormShape = {
  code: '',
  displayName: '',
  description: '',
  layer: 'product',
  multiplicity: 'perCart',
  defaultPageCount: '1',
  signatureRequirement: 'none',
  defaultSignatureCount: '0',
  contentSource: 'inline',
  variantKey: '',
  productCategories: [],
  products: [],
  entityTypes: [],
  audience: 'fsr-officer',
  sortOrder: '',
  isInternal: false,
  isActive: true,
};

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const withoutAll = (xs: string[]): string[] => xs.filter((x) => x.toUpperCase() !== 'ALL');

function rowToForm(row: DisclosureTemplateRow | null): FormShape {
  if (!row) return EMPTY_FORM;
  return {
    code: str(row.code),
    displayName: str(row.displayName),
    description: str(row.description),
    layer: str(row.layer),
    multiplicity: str(row.multiplicity),
    defaultPageCount: str(row.defaultPageCount),
    signatureRequirement: str(row.signatureRequirement),
    defaultSignatureCount: str(row.defaultSignatureCount),
    contentSource: str(row.contentSource),
    variantKey: str(row.variantKey),
    productCategories: withoutAll(splitCsv(row.productCategory)),
    products: splitCsv(row.applicableProducts),
    entityTypes: withoutAll(splitCsv(row.applicableEntityTypes)),
    audience: str(row.audience) || 'fsr-officer',
    sortOrder: str(row.sortOrder),
    isInternal: row.isInternal === true,
    isActive: row.isActive !== false,
  };
}

function formToInput(f: FormShape): DisclosureTemplateUpsertInput {
  const numOrNull = (s: string): number | null => {
    if (!s.trim()) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };
  return {
    code: f.code.trim(),
    displayName: f.displayName.trim() || null,
    description: f.description.trim() || null,
    layer: f.layer || null,
    multiplicity: f.multiplicity || null,
    defaultPageCount: numOrNull(f.defaultPageCount),
    signatureRequirement: f.signatureRequirement || null,
    defaultSignatureCount: numOrNull(f.defaultSignatureCount),
    contentSource: f.contentSource || null,
    variantKey: f.variantKey.trim() || null,
    // The resolver reads 'ALL' (or empty) as "every product type / customer type".
    productCategory: joinCsv(f.productCategories) ?? 'ALL',
    applicableProducts: joinCsv(f.products),
    applicableEntityTypes: joinCsv(f.entityTypes) ?? 'ALL',
    audience: f.audience || null,
    sortOrder: numOrNull(f.sortOrder),
    isInternal: f.isInternal,
    isActive: f.isActive,
  };
}

export interface TemplateEditorModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: DisclosureTemplateRow | null;
  onClose: () => void;
  onSaved: (code: string) => void;
  onError: (message: string) => void;
}

export function TemplateEditorModal({
  open,
  mode,
  initial,
  onClose,
  onSaved,
  onError,
}: TemplateEditorModalProps): JSX.Element | null {
  const { options, optionsError, envelope } = useDisclosuresConfig();
  const [form, setForm] = useState<FormShape>(() => rowToForm(initial ?? null));
  const [busy, setBusy] = useState<boolean>(false);
  const [inlineErr, setInlineErr] = useState<string | null>(null);

  // Hydrate only when the dialog opens (not on every catalog refresh while open).
  useEffect(() => {
    if (open) {
      setForm(rowToForm(mode === 'edit' ? initial ?? null : null));
      setInlineErr(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const categoryItems = useMemo<PickItem[]>(
    () => (options?.productCategories ?? []).map((c) => ({ value: c, label: c })),
    [options],
  );
  const productItems = useMemo<PickItem[]>(() => {
    const cats = new Set(form.productCategories.map((c) => c.toUpperCase()));
    return (options?.products ?? [])
      .filter((p) => p.isActive || form.products.some((x) => x.toUpperCase() === p.code.toUpperCase()))
      .filter((p) => cats.size === 0 || cats.has(String(p.category).toUpperCase()))
      .map((p) => ({ value: p.code, label: `${p.name} (${p.code})`, meaning: p.category }));
  }, [options, form.productCategories, form.products]);
  const entityItems = useMemo<PickItem[]>(
    () => (options?.entityTypes ?? []).map((e) => ({ value: e.value, label: e.label || e.value })),
    [options],
  );
  const variantKeyItems = useMemo<PickItem[]>(() => {
    const keys = Array.from(new Set(envelope.variants.map((v) => String(v.variantKey ?? '')).filter(Boolean))).sort();
    return keys.map((k) => ({ value: k, label: k }));
  }, [envelope.variants]);

  const productLayer = ['universal', 'product', 'ownership'].includes(form.layer);
  const canSave = !busy && form.code.trim().length > 0 && form.displayName.trim().length > 0 && options !== null;

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    setBusy(true);
    setInlineErr(null);
    try {
      const result = await upsertTemplate(formToInput(form));
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setInlineErr(msg);
        onError(msg);
        return;
      }
      onSaved(form.code.trim());
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed.';
      setInlineErr(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      title={mode === 'edit' ? `Edit ${form.code}` : 'New template'}
      width={760}
      closeOnBackdrop={!busy}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={!canSave}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {inlineErr && (
        <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
          {inlineErr}
        </div>
      )}
      {optionsError && (
        <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
          The pick lists did not load ({optionsError}), so saving is switched off.
        </div>
      )}
      <div className="banner banner--info" style={{ marginBottom: 12, fontSize: 12 }}>
        These settings take effect for the next package built — they are not versioned or
        approval-gated. The document text and its &quot;only when&quot; conditions are on
        each version and go through submit, approve and activate.
      </div>

      <FieldGrid columns={2}>
        <Field label="Code" required hint={mode === 'edit' ? 'The code cannot be changed.' : 'Unique; letters, digits, _ and -.'}>
          <TextInput
            value={form.code}
            onChange={(e) => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
            readOnly={mode === 'edit'}
            maxLength={32}
          />
        </Field>
        <Field label="Name" required>
          <TextInput value={form.displayName} onChange={(e) => set('displayName', e.target.value)} maxLength={128} />
        </Field>
        <Field label="Description" span={2}>
          <TextArea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} maxLength={1024} />
        </Field>
      </FieldGrid>

      <h4 style={{ margin: '12px 0 6px' }}>Who gets it</h4>
      <FieldGrid columns={2}>
        <Field label="How it is added to a package" required>
          <VocabSelect items={options?.layers ?? []} value={form.layer} onChange={(v) => set('layer', v)} ariaLabel="Layer" />
        </Field>
        <Field label="Audience" required>
          <VocabSelect items={options?.audiences ?? []} value={form.audience} onChange={(v) => set('audience', v)} ariaLabel="Audience" />
        </Field>
        {productLayer ? (
          <>
            <Field label="Product types" span={2} hint="None selected = every product type.">
              <ChipPicker
                items={categoryItems}
                values={form.productCategories}
                onChange={(v) => set('productCategories', v)}
                allLabel="Every product type"
                ariaLabel="Product types"
              />
            </Field>
            <Field label="Products" span={2} hint="None selected = every product in the chosen types.">
              <ChipPicker
                items={productItems}
                values={form.products}
                onChange={(v) => set('products', v)}
                allLabel="Every product"
                ariaLabel="Products"
              />
            </Field>
            <Field label="Customer types" span={2} hint="None selected = every customer type.">
              <ChipPicker
                items={entityItems}
                values={form.entityTypes}
                onChange={(v) => set('entityTypes', v)}
                allLabel="Every customer type"
                ariaLabel="Customer types"
              />
            </Field>
          </>
        ) : (
          <div style={{ gridColumn: 'span 2', fontSize: 12, color: 'var(--ink-500)', marginBottom: 12 }}>
            Product and customer-type filters only apply to the &quot;Every account opening&quot;,
            &quot;Product-driven&quot; and &quot;Ownership-driven&quot; ways of adding a document; they are
            ignored for this one.
          </div>
        )}
      </FieldGrid>

      <h4 style={{ margin: '12px 0 6px' }}>Signing</h4>
      <FieldGrid columns={2}>
        <Field label="Who signs" required span={2}>
          <VocabSelect
            items={options?.signatureRequirements ?? []}
            value={form.signatureRequirement}
            onChange={(v) => set('signatureRequirement', v)}
            ariaLabel="Who signs"
          />
        </Field>
        <Field label="Signatures expected" hint="For reference only; the package builder decides signers from 'Who signs'.">
          <NumberInput value={form.defaultSignatureCount} onChange={(e) => set('defaultSignatureCount', e.target.value)} min={0} />
        </Field>
        <Field label="Pages expected" hint="For reference only.">
          <NumberInput value={form.defaultPageCount} onChange={(e) => set('defaultPageCount', e.target.value)} min={0} />
        </Field>
      </FieldGrid>

      <h4 style={{ margin: '12px 0 6px' }}>Other</h4>
      <FieldGrid columns={2}>
        <Field label="Copies" hint="For reference only.">
          <VocabSelect items={options?.multiplicities ?? []} value={form.multiplicity} onChange={(v) => set('multiplicity', v)} ariaLabel="Copies" />
        </Field>
        <Field label="Content source" hint="For reference only; the body always comes from the version.">
          <VocabSelect items={options?.contentSources ?? []} value={form.contentSource} onChange={(v) => set('contentSource', v)} ariaLabel="Content source" />
        </Field>
        <Field label="Variant family" hint="Groups templates that swap bodies by plan or customer type (Variants screen).">
          <VocabSelect
            items={variantKeyItems}
            value={form.variantKey}
            onChange={(v) => set('variantKey', v)}
            ariaLabel="Variant family"
            allowEmpty
            emptyLabel="None — rendered as-is"
          />
        </Field>
        <Field label="Order in the package" hint="Empty = the built-in order.">
          <NumberInput value={form.sortOrder} onChange={(e) => set('sortOrder', e.target.value)} />
        </Field>
        <div>
          <Checkbox label="Internal only (staff copy)" checked={form.isInternal} onChange={(e) => set('isInternal', e.target.checked)} />
        </div>
        <div>
          <Checkbox label="Switched on" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
        </div>
      </FieldGrid>
    </Modal>
  );
}
